import { forwardRef, type HTMLProps } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowSquareOut, Books, Browser, Car, Newspaper, ShoppingCart } from '@phosphor-icons/react';

import {
  SearchResultContent,
  SearchResultProductDeprecated,
  SearchResultType,
} from '@tmh-website/services/search.service';
import { LocalePrefix } from '@tmh-website/shared/const';

import * as Styled from './ResultEntriesDeprecated.styles';

/**
 * The result entry component for pages
 */

interface ResultsEntryContentProps extends Omit<HTMLProps<HTMLAnchorElement>, 'type' | 'ref' | 'as' | 'forwardedAs'> {
  result: SearchResultContent;
  ref?: any;
  locale: LocalePrefix;
}

/**
 * @deprecated
 */
export const ResultsEntryContent = forwardRef<HTMLAnchorElement, ResultsEntryContentProps>(
  ({ result, locale, ...props }, ref) => {
    const { t } = useTranslation();

    let Icon = Browser;
    let i18nKeyType = 'contentPage';

    switch (result.type) {
      case SearchResultType.Car:
        Icon = Car;
        i18nKeyType = 'car';
        break;
      case SearchResultType.News:
        Icon = Newspaper;
        i18nKeyType = 'news';
        break;
      case SearchResultType.KnowledgeCenter:
        Icon = Books;
        i18nKeyType = 'knowledgeCenter';
        break;
    }

    return (
      <Styled.Container
        ref={ref}
        {...props}
        href={'/' + locale + result.path}
        target='_blank'
        rel='noopener noreferrer'>
        <Styled.IconContainer>
          <Icon size={24} />
        </Styled.IconContainer>
        <Styled.InfoContainer>
          <Styled.Title>{result.title}</Styled.Title>
          <Styled.Type>{t(`global:search.type.${i18nKeyType}`)}</Styled.Type>
        </Styled.InfoContainer>
        <Styled.NewTabInfo>
          <ArrowSquareOut size={20} />
          <span>{t('global:search.deprecated_newTab')}</span>
        </Styled.NewTabInfo>
      </Styled.Container>
    );
  }
);

ResultsEntryContent.displayName = 'ResultsEntryContent';

/**
 * The result entry component for products
 */

interface ResultsEntryProductProps extends Omit<HTMLProps<HTMLAnchorElement>, 'type' | 'ref' | 'as' | 'forwardedAs'> {
  product: SearchResultProductDeprecated;
  locale: LocalePrefix;
}

/**
 * @deprecated
 */
export const ResultsEntryProduct = forwardRef<HTMLAnchorElement, ResultsEntryProductProps>(
  ({ product, locale, ...props }, ref) => {
    const { t } = useTranslation();

    return (
      <Styled.Container ref={ref} {...props} href={product.url} target='_blank' rel='noopener noreferrer'>
        {product.image ? (
          <Styled.ProductImage src={product.image} alt={product.title} width={40} height={40} />
        ) : (
          <Styled.IconContainer>
            <ShoppingCart size={24} />
          </Styled.IconContainer>
        )}
        <Styled.InfoContainer>
          <Styled.Title>{product.title}</Styled.Title>
          <Styled.Type>{product.price}</Styled.Type>
        </Styled.InfoContainer>
        <Styled.NewTabInfo>
          <ArrowSquareOut size={24} />
          <span>{t('global:search.deprecated_newTab')}</span>
        </Styled.NewTabInfo>
      </Styled.Container>
    );
  }
);

ResultsEntryProduct.displayName = 'ResultsEntryProduct';

/**
 * @deprecated
 */
export const ResultsEntrySkeleton = () => {
  return (
    <Styled.Container as='div' $noHover>
      <Styled.FakeIconContainer />
      <Styled.InfoContainer>
        <Styled.FakeText />
        <Styled.FakeText />
      </Styled.InfoContainer>
    </Styled.Container>
  );
};
